import {
    UiProgressButton,
    UiText,
} from '@experiences/ui-common';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import clsx from 'clsx';
import { sanitize } from 'dompurify';
import React, {
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import {
    Controller,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import useSWR from 'swr';

import { notificationType } from '../../../common/constants/Constant';
import { MessageTemplateName } from '../../../common/constants/MessageTemplateConstant';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import {
    createMessageTemplate,
    getMessageTemplate,
    messageTemplateUrl,
    updateMessageTemplate,
} from '../../../services/identity/MessageTemplateService';
import {
    mapLoginCustomizationDataToCreateMessageTemplateDto,
    mapLoginCustomizationDataToMessageTemplate,
    mapToLoginCustomizationData,
} from '../../../util/MessageTemplateUtil';
import { UiCodeEditor } from '../../common/UiCodeEditor';
import UiForm from '../../common/UiForm';
import type { ILoginCustomizationData } from './types/login-customization';

const useStyles = makeStyles(theme =>
    createStyles({
        root: {
            width: '100%',
            height: 'calc(100% - 40px)',
            maxWidth: '900px',
        },
        cancelButton: { marginRight: '10px' },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        section: {
            flex: 1,
            minWidth: '200px',
            maxWidth: '50%',
        },
        sectionHeading: {
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: theme.palette.semantic.colorForegroundEmp,
        },
        sectionDescription: {
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        inputMargin: { marginBottom: '8px' },
        sectionText: { marginBottom: '16px' },
        dividedContent: {
            padding: '21px 16px 0px 0px',
            flexDirection: 'row',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            width: '100%',
            gap: '16px',
        },
        preview: {
            backgroundColor: theme.palette.semantic.colorBackgroundSecondary,
            padding: '24px',
        },
    }),
);

const MAX_LOGIN_CUSTOMIZATION = 20000;

const LoginCustomizationComponent: React.FC = () => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();
    const createNotification = useUiSnackBar();

    const {
        handleSubmit,
        reset,
        formState: {
            errors, isDirty, isValid, isSubmitting,
        },
        control,
        watch,
    } = useForm<ILoginCustomizationData>({
        mode: 'onChange',
        defaultValues: { loginLayout: '' },
    });

    const {
        data: fetchedLoginLayoutMessageTemplate, mutate,
    } = useSWR(
        {
            url: messageTemplateUrl,
            name: MessageTemplateName.LoginLayout,
        },
        getMessageTemplate,
        { shouldRetryOnError: false },
    );
    const originalData = useMemo(
        () => mapToLoginCustomizationData(fetchedLoginLayoutMessageTemplate),
        [ fetchedLoginLayoutMessageTemplate ],
    );
    const loginLayout = watch('loginLayout');

    const onSubmit = useCallback(
        async (data: ILoginCustomizationData) => {
            try {
                const dataPromise = fetchedLoginLayoutMessageTemplate
                    ? updateMessageTemplate(
                        mapLoginCustomizationDataToMessageTemplate(data, fetchedLoginLayoutMessageTemplate),
                    )
                    : createMessageTemplate(mapLoginCustomizationDataToCreateMessageTemplateDto(data));
                const savedData = await dataPromise;
                mutate(savedData);
                createNotification(
                    translate({ id: 'CLIENT_LOGIN_CUSTOMIZATION_UPDATED' }),
                    notificationType.SUCCESS
                );
            } catch (error) {
                createNotification(
                    translate({ id: 'CLIENT_LOGIN_CUSTOMIZATION_UPDATE_ERROR' }),
                    notificationType.ERROR,
                );
            }
        },
        [ createNotification, fetchedLoginLayoutMessageTemplate, mutate, translate ],
    );

    const handleCancel = useCallback(() => {
        reset();
    }, [ reset ]);

    useEffect(() => {
        if (fetchedLoginLayoutMessageTemplate) {
            reset(originalData);
        }
    }, [ reset, fetchedLoginLayoutMessageTemplate, originalData ]);

    return (
        <div className={classes.root}>
            <UiForm
                onSubmit={handleSubmit(onSubmit)}
                actions={
                    <div className={classes.actions}>
                        {isDirty && (
                            <Button
                                className={classes.cancelButton}
                                onClick={() => handleCancel()}
                                color="primary"
                                data-cy="login-customization-cancel-button"
                            >
                                {translate({ id: 'CLIENT_CANCEL' })}
                            </Button>
                        )}
                        <UiProgressButton
                            type="submit"
                            loading={isSubmitting}
                            disabled={!isDirty || !isValid}
                            variant="contained"
                            data-cy="login-customization-submit-button"
                        >
                            {translate({ id: 'CLIENT_SAVE' })}
                        </UiProgressButton>
                    </div>
                }
            >
                <div className={classes.dividedContent}>
                    <div className={classes.section}>
                        <div className={classes.sectionText}>
                            <UiText className={clsx(classes.sectionHeading, classes.inputMargin)}>
                                {translate({ id: 'CLIENT_EDIT' })}
                            </UiText>
                            <UiText className={classes.sectionDescription}>
                                {translate({ id: 'CLIENT_LOGIN_CUSTOMIZATION_EDIT_DESCRIPTION' })}
                            </UiText>
                        </div>
                        <Controller
                            control={control}
                            name="loginLayout"
                            rules={{ maxLength: MAX_LOGIN_CUSTOMIZATION }}
                            render={({ field }) => (
                                <>
                                    <UiCodeEditor
                                        dataCy="login-customization-login-layout-input"
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    />
                                    <FormHelperText error={!!errors.loginLayout}>
                                        {(errors.loginLayout?.type === 'required' &&
                                        translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' })) ||
                                        (errors.loginLayout?.type === 'maxLength' &&
                                            translate({ id: 'CLIENT_LOGIN_CUSTOMIZATION_MAX_LENGTH_ERROR' })) ||
                                        `${field.value?.length ?? 0}/${MAX_LOGIN_CUSTOMIZATION}`}
                                    </FormHelperText>
                                </>
                            )}
                        />
                    </div>
                    <div className={classes.section}>
                        <div className={classes.sectionText}>
                            <UiText className={clsx(classes.sectionHeading, classes.inputMargin)}>
                                {translate({ id: 'CLIENT_PREVIEW' })}
                            </UiText>
                            <UiText className={classes.sectionDescription}>
                                {translate({ id: 'CLIENT_LOGIN_CUSTOMIZATION_PREVIEW_DESCRIPTION' })}
                            </UiText>
                        </div>
                        <div
                            data-cy="login-customization-login-layout-preview"
                            className={classes.preview}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: sanitize(loginLayout, { FORCE_BODY: true }) }}
                        />
                    </div>
                </div>
            </UiForm>
        </div>
    );
};

export default LoginCustomizationComponent;
